import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Check if this is a Facebook OAuth callback
        const provider = searchParams.get('provider');
        const success = searchParams.get('success');
        const token = searchParams.get('token');
        const error = searchParams.get('error');

        // Handle Facebook OAuth callback
        if (provider === 'facebook') {
          if (success === 'true' && token) {
            // Facebook login successful - fetch user data
            const response = await fetch(`${API_URL}/api/auth/me`, {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const userData = await response.json();
              localStorage.setItem('isLoggedIn', 'true');
              localStorage.setItem('user', JSON.stringify(userData));
              
              toast({
                title: "Login Successful",
                description: `Welcome, ${userData.name}!`
              });
              
              navigate('/dashboard', { state: { user: userData } });
              return;
            } else {
              throw new Error('Failed to fetch user data');
            }
          } else if (error) {
            throw new Error(`Facebook login failed: ${error}`);
          }
        }

        // Handle Google OAuth callback (existing logic)
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('No session_id found in URL');
          navigate('/login');
          return;
        }

        const sessionId = sessionIdMatch[1];

        // Exchange session_id for user data via backend
        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Authentication failed');
        }

        const userData = await response.json();

        // Store user data in localStorage
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('user', JSON.stringify(userData));

        toast({
          title: "Login Successful",
          description: `Welcome, ${userData.name}!`
        });

        // Navigate to dashboard with user data
        navigate('/dashboard', { state: { user: userData } });

      } catch (error) {
        console.error('Auth callback error:', error);
        toast({
          title: "Authentication Failed",
          description: error.message || "Could not complete login",
          variant: "destructive"
        });
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-6"></div>
        <h2 className="text-2xl font-bold text-white mb-2">Completing Login...</h2>
        <p className="text-gray-300">Please wait while we authenticate you</p>
      </div>
    </div>
  );
};

export default AuthCallback;
